import { CommonModule } from '@angular/common';
import { Component, computed, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { forkJoin } from 'rxjs';

import { formatCompactOrIsoDate, formatCurrencyAmount } from '../shared/trade-formatters';
import { InvoicesService } from './invoices.service';
import {
  InvoiceLinkType,
  InvoiceLinkTypeCode,
  InvoiceSummary,
  InvoiceTypeOption,
  LinkTypeOption,
  Vendor,
} from './invoices.types';

@Component({
  selector: 'app-invoices',
  imports: [CommonModule, FormsModule, RouterLink],
  templateUrl: './invoices.html',
  styleUrl: './invoices.css',
})
export class Invoices implements OnInit {
  private readonly pageSize = 8;

  protected readonly invoices = signal<InvoiceSummary[]>([]);
  protected readonly vendors = signal<Vendor[]>([]);
  protected readonly invoiceTypeOptions = signal<InvoiceTypeOption[]>([]);
  protected readonly linkTypeOptions = signal<LinkTypeOption[]>([]);
  protected readonly isLoading = signal(false);
  protected readonly loadError = signal('');
  protected readonly searchTerm = signal('');
  protected readonly selectedVendorFilter = signal<'all' | number>('all');
  protected readonly selectedLinkTypeFilter = signal<'all' | InvoiceLinkType>('all');
  protected readonly selectedConceptFilter = signal<'all' | string>('all');
  protected readonly pageNumber = signal(1);

  protected readonly filteredInvoices = computed(() => {
    const term = this.searchTerm().trim().toLowerCase();
    const vendorFilter = this.selectedVendorFilter();
    const linkFilter = this.selectedLinkTypeFilter();
    const conceptFilter = this.selectedConceptFilter();

    return this.invoices().filter((invoice) => {
      const linkedToLabel = this.linkedToLabel(invoice);
      const linkedToTypes = this.linkedToTypes(invoice);
      const matchesSearch = !term || [
        invoice.invoiceNumber,
        invoice.vendorName,
        this.vendorTypeById().get(invoice.vendorId) ?? '',
        invoice.invoiceConceptNames.join(', '),
        linkedToLabel,
        invoice.linkedProgram ?? '',
        invoice.currency,
        invoice.createdAt,
        invoice.lastModifiedBy || invoice.createdBy,
      ].some((field) => field.toLowerCase().includes(term));

      const matchesVendor = vendorFilter === 'all' || invoice.vendorId === vendorFilter;
      const matchesLinkType = linkFilter === 'all' || linkedToTypes.includes(linkFilter);
      const matchesConcept = conceptFilter === 'all' || invoice.invoiceConceptNames.includes(conceptFilter);

      return matchesSearch && matchesVendor && matchesLinkType && matchesConcept;
    });
  });

  protected readonly totalPages = computed(() => Math.max(1, Math.ceil(this.filteredInvoices().length / this.pageSize)));

  protected readonly pagedInvoices = computed(() => {
    const start = (this.pageNumber() - 1) * this.pageSize;
    return this.filteredInvoices().slice(start, start + this.pageSize);
  });

  protected readonly rangeLabel = computed(() => {
    const total = this.filteredInvoices().length;
    if (total === 0) {
      return 'No records';
    }

    const start = (this.pageNumber() - 1) * this.pageSize + 1;
    const end = Math.min(this.pageNumber() * this.pageSize, total);
    return `Showing ${start} to ${end} of ${total} invoices`;
  });

  protected readonly vendorFilterOptions = computed(() => {
    return this.vendors()
      .map((vendor) => ({ id: vendor.id, name: vendor.name }))
      .sort((left, right) => left.name.localeCompare(right.name));
  });

  private readonly vendorTypeById = computed(() => {
    return new Map<number, string>(this.vendors().map((vendor) => [vendor.id, vendor.vendorTypeName]));
  });

  constructor(private readonly invoicesService: InvoicesService) {}

  ngOnInit(): void {
    this.isLoading.set(true);

    forkJoin({
      vendors: this.invoicesService.getVendorOptions(),
      conceptOptions: this.invoicesService.getInvoiceTypeOptions(),
      linkTypeOptions: this.invoicesService.getLinkTypeOptions(),
      invoices: this.invoicesService.getInvoices(),
    }).subscribe({
      next: ({ vendors, conceptOptions, linkTypeOptions, invoices }) => {
        this.vendors.set(vendors);
        this.invoiceTypeOptions.set(conceptOptions);
        this.linkTypeOptions.set(linkTypeOptions);
        this.invoices.set(invoices);
        this.pageNumber.set(1);
        this.isLoading.set(false);
      },
      error: () => {
        this.isLoading.set(false);
        this.loadError.set('Unable to load invoices data from the API.');
      },
    });
  }

  protected onSearchChange(value: string): void {
    this.searchTerm.set(value);
    this.pageNumber.set(1);
  }

  protected onVendorFilterChange(value: string): void {
    const parsed = Number(value);
    this.selectedVendorFilter.set(Number.isFinite(parsed) && parsed > 0 ? parsed : 'all');
    this.pageNumber.set(1);
  }

  protected onLinkTypeFilterChange(value: 'all' | InvoiceLinkType): void {
    this.selectedLinkTypeFilter.set(value);
    this.pageNumber.set(1);
  }

  protected onConceptFilterChange(value: string): void {
    this.selectedConceptFilter.set(value || 'all');
    this.pageNumber.set(1);
  }

  protected clearFilters(): void {
    this.selectedVendorFilter.set('all');
    this.selectedLinkTypeFilter.set('all');
    this.selectedConceptFilter.set('all');
    this.searchTerm.set('');
    this.pageNumber.set(1);
  }

  protected goToPreviousPage(): void {
    if (this.pageNumber() > 1) {
      this.pageNumber.set(this.pageNumber() - 1);
    }
  }

  protected goToNextPage(): void {
    if (this.pageNumber() < this.totalPages()) {
      this.pageNumber.set(this.pageNumber() + 1);
    }
  }

  protected deleteInvoice(invoice: InvoiceSummary): void {
    const confirmed = window.confirm(`Delete invoice "${invoice.invoiceNumber}"?`);
    if (!confirmed) {
      return;
    }

    this.invoicesService.deleteInvoice(invoice.id).subscribe({
      next: (result) => {
        if (!result.deleted) {
          window.KTToast?.show({ message: result.message || 'Unable to delete invoice.', variant: 'destructive' });
          return;
        }

        this.reloadInvoices();
        window.KTToast?.show({ message: 'Invoice deleted successfully.', variant: 'success' });
      },
      error: () => {
        window.KTToast?.show({ message: 'Unable to delete invoice.', variant: 'destructive' });
      },
    });
  }

  protected formatDate(value: string): string {
    return formatCompactOrIsoDate(value);
  }

  protected vendorTypeFor(invoice: InvoiceSummary): string {
    return this.vendorTypeById().get(invoice.vendorId) || '-';
  }

  protected formatAmount(currency: string, amount: number): string {
    return formatCurrencyAmount(currency, amount);
  }

  protected trackByInvoice(_: number, invoice: InvoiceSummary): number {
    return invoice.id;
  }

  protected linkedToLabel(invoice: InvoiceSummary): string {
    return this.linkedToTypes(invoice).join(' + ');
  }

  private linkedToTypes(invoice: InvoiceSummary): InvoiceLinkType[] {
    if (invoice.lines.length === 0) {
      return [invoice.linkType];
    }

    const labelsByCode: Record<InvoiceLinkTypeCode, InvoiceLinkType> = {
      NONE: 'Generic',
      DEAL: 'Deal ID',
      SECURITY: 'Security ID',
      BOND_DOCUMENTATION: 'Program',
    };
    const displayOrder: InvoiceLinkTypeCode[] = ['NONE', 'DEAL', 'SECURITY', 'BOND_DOCUMENTATION'];
    const lineTypes = new Set(invoice.lines.map((line) => line.linkTypeCode));

    return displayOrder.filter((code) => lineTypes.has(code)).map((code) => labelsByCode[code]);
  }

  private reloadInvoices(): void {
    this.isLoading.set(true);
    this.invoicesService.getInvoices().subscribe({
      next: (rows) => {
        this.invoices.set(rows);
        this.normalizePage();
        this.isLoading.set(false);
      },
      error: () => {
        this.isLoading.set(false);
        this.loadError.set('Unable to reload invoices from the API.');
      },
    });
  }

  private normalizePage(): void {
    if (this.pageNumber() > this.totalPages()) {
      this.pageNumber.set(this.totalPages());
    }
  }
}
